import React, { useRef } from 'react';
import { Box, Typography, TextField, Button, Avatar } from '@mui/material';
import { PhotoCamera, Lock, Save } from '@mui/icons-material';
import dayjs from 'dayjs';
import { DatePicker } from 'antd';
import { COLORS_ADMIN_PAGE } from '../../conf/constants';
import { UserBasicInfoProps } from '../types/type';

export const UserBasicInfo: React.FC<UserBasicInfoProps> = ({
  selectedUser,
  phoneError,
  emailError,
  onInputChange,
  onPhoneChange,
  onEmailChange,
  onBirthdayChange,
  onUploadAvatarClick,
  onOpenChangePasswordModal,
  onSaveChanges,
  onFileChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '20%' }}>
        <Avatar
          src={selectedUser.profileImageUrl || ''}
          sx={{ width: 150, height: 150, border: `2px solid ${COLORS_ADMIN_PAGE.BORDER}`, borderRadius: '12px' }}
        />
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={onFileChange}
        />
        <Button
          startIcon={<PhotoCamera />}
          variant="contained"
          sx={{ width: '100%', justifyContent: 'flex-start' }}
          onClick={() => fileInputRef.current?.click()}
          disabled={!selectedUser}
        >
          UPLOAD AVATAR PHOTO
        </Button>
        <Button
          startIcon={<Lock />}
          variant="contained"
          sx={{ width: '100%', justifyContent: 'flex-start' }}
          onClick={onOpenChangePasswordModal}
          disabled={!selectedUser}
        >
          CHANGE PASSWORD
        </Button>
        <Button
          startIcon={<Save />}
          variant="contained"
          sx={{ width: '100%', justifyContent: 'flex-start' }}
          onClick={onSaveChanges}
        >
          SAVE CHANGES
        </Button>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Name
            </Typography>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Account & Contact
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="First Name"
                value={selectedUser.firstName || ''}
                onChange={(e) => onInputChange('firstName', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Username"
                disabled
                value={selectedUser.username || ''}
                onChange={(e) => onInputChange('username', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Middle Name"
                value={selectedUser.middleName || ''}
                onChange={(e) => onInputChange('middleName', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={selectedUser.email || ''}
                onChange={(e) => onEmailChange(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={selectedUser.lastName || ''}
                onChange={(e) => onInputChange('lastName', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={selectedUser.phoneNumber || ''}
                error={!!phoneError}
                helperText={phoneError}
                onChange={(e) => onPhoneChange(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Personal Details
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                position: 'relative',
                '& .ant-picker': {
                  width: '100%',
                  height: '56px',
                  borderRadius: '8px',
                  border: `1px solid rgba(0, 0, 0, 0.23)`,
                  fontSize: '16px',
                  paddingLeft: '14px',
                  paddingTop: '16px',
                  '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)' },
                  '&.ant-picker-focused': { borderColor: COLORS_ADMIN_PAGE.PRIMARY, borderWidth: '2px', outline: 'none' },
                },
                '& .ant-picker-input > input': { fontSize: '16px', paddingTop: '8px', '&::placeholder': { color: 'transparent' } },
                '& .ant-picker-suffix': { color: 'rgba(0, 0, 0, 0.54)' },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  position: 'absolute',
                  top: selectedUser.birthday ? '8px' : '50%',
                  left: '10px',
                  transform: selectedUser.birthday ? 'translateY(0)' : 'translateY(-50%)',
                  backgroundColor: COLORS_ADMIN_PAGE.WHITE,
                  padding: selectedUser.birthday ? '0 4px' : '0',
                  fontSize: selectedUser.birthday ? '12px' : '16px',
                  zIndex: 1,
                  pointerEvents: 'none',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Birthday
              </Typography>
              <DatePicker
                value={selectedUser.birthday ? dayjs(selectedUser.birthday) : null}
                onChange={onBirthdayChange}
                format="DD/MM/YYYY"
                size="large"
                suffixIcon={
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'rgba(0, 0, 0, 0.54)' }}>
                    📅
                  </Box>
                }
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6">
            Social Media
          </Typography>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Facebook Profile"
              value={selectedUser.facebookProfile || ''}
              onChange={(e) => onInputChange('facebookProfile', e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};